import { createFileRoute } from "@tanstack/react-router";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SettingsHeader } from "~/components/layout/settings-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  SelectContent,
  SelectItem,
  SelectValue,
  SelectWithTrigger,
} from "~/components/ui/select";
import { useTheme } from "~/providers/theme-provider";

export const Route = createFileRoute("/_main/settings/_layout/appearance")({
  component: Appearance,
});

function Appearance() {
  const { t } = useTranslation("common");
  const { setTheme, theme, setFontSize, fontSize } = useTheme();

  return (
    <>
      <SettingsHeader title={t("Appearance.page_title")} />
      <div className="flex flex-col gap-4">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t("Appearance.theme")}</CardTitle>
            <CardDescription>
              {t("Appearance.theme_description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              defaultValue={theme}
              onValueChange={(value) => setTheme(value)}
              className="grid grid-cols-3 gap-4"
            >
              <Label
                htmlFor="light"
                className="flex cursor-pointer flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground [&:has([data-state=checked])]:border-primary"
              >
                <RadioGroupItem value="light" id="light" className="sr-only" />
                <Sun className="mb-2 h-6 w-6" />
                {t("Appearance.light")}
              </Label>
              <Label
                htmlFor="dark"
                className="flex flex-col  cursor-pointer items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground [&:has([data-state=checked])]:border-primary"
              >
                <RadioGroupItem value="dark" id="dark" className="sr-only" />
                <Moon className="mb-2 h-6 w-6" />
                {t("Appearance.dark")}
              </Label>
              <Label
                htmlFor="system"
                className="flex flex-col  cursor-pointer items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground [&:has([data-state=checked])]:border-primary"
              >
                <RadioGroupItem
                  value="system"
                  id="system"
                  className="sr-only"
                />
                <span className="mb-2 h-6 w-6 flex items-center justify-center">
                  <Monitor className="mb-2 h-6 w-6" />
                </span>
                {t("Appearance.system")}
              </Label>
            </RadioGroup>
          </CardContent>
        </Card>
        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t("Appearance.font_size")}</CardTitle>
            <CardDescription>
              {t("Appearance.font_size_description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SelectWithTrigger
              value={fontSize}
              onValueChange={(value) => {
                setFontSize(value);
              }}
            >
              <SelectValue
                placeholder={t("Appearance.font_size_placeholder")}
              />
              <SelectContent>
                <SelectItem value="small">
                  {t("Appearance.font_size_small")}
                </SelectItem>
                <SelectItem value="medium">
                  {t("Appearance.font_size_medium")}
                </SelectItem>
                <SelectItem value="large">
                  {t("Appearance.font_size_large")}
                </SelectItem>
              </SelectContent>
            </SelectWithTrigger>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
